abstract class BaseStorageService {
    constructor(public readonly ns: string) {}
    protected getOrCreateKey(key: string | null): string {
        key ??= performance.now().toString();
        return `${this.ns}:${key}`;
    }
    protected getId(fullKey: string) {
        return fullKey.substring(this.ns.length + 1);
    }

    public get<T>(id: string): Promise<T | null> {
        return this.getImpl(this.getOrCreateKey(id));
    }
    public save<T>(id: string | null, value: T): Promise<string> {
        return this.saveImpl(this.getOrCreateKey(id), value);
    }
    public remove(id: string): Promise<void> {
        return this.removeImpl(this.getOrCreateKey(id));
    }
    public getAll<T>(): Promise<{ id: string; item: T }[]> {
        return this.getAllImpl();
    }

    protected abstract getImpl<T>(fullKey: string): Promise<T | null>;
    protected abstract saveImpl<T>(fullKey: string, value: T): Promise<string>;
    protected abstract removeImpl(fullKey: string): Promise<void>;
    protected abstract getAllImpl<T>(): Promise<{ id: string; item: T }[]>;
}

class InMemoryStorageService extends BaseStorageService {
    private readonly storage = new Map<string, any>();

    protected async getImpl<T>(key: string): Promise<T | null> {
        return Promise.resolve(this.storage.get(key) ?? null);
    }

    public async saveImpl<T>(key: string, value: T) {
        this.storage.set(key, value);
        return Promise.resolve(key);
    }

    public removeImpl(key: string) {
        this.storage.delete(key);
        return Promise.resolve();
    }

    public getAllImpl<T>() {
        return Promise.resolve([...this.storage.entries().map(([key, item]) => ({ id: this.getId(key), item: item as T }))]);
    }
}

class LocalStorageStorageService extends BaseStorageService {
    protected getImpl<T>(fullKey: string): Promise<T | null> {
        const item = localStorage.getItem(fullKey);
        return Promise.resolve(item ? JSON.parse(item) : null);
    }

    protected saveImpl<T>(fullKey: string, value: T): Promise<string> {
        try {
            localStorage.setItem(fullKey, JSON.stringify(value));
        } catch (error) {
            if (error instanceof DOMException && error.name === 'QuotaExceededError') {
                throw new StorageError('Storage quota exceeded', error, 'no-space');
            } else {
                throw error;
            }
        }
        return Promise.resolve(fullKey);
    }

    protected removeImpl(fullKey: string): Promise<void> {
        localStorage.removeItem(fullKey);
        return Promise.resolve();
    }

    protected getAllImpl<T>(): Promise<{ id: string; item: T }[]> {
        const items: { id: string; item: T }[] = [];
        for (const [key, value] of Object.entries(localStorage)) {
            items.push({ id: this.getId(key), item: JSON.parse(value) });
        }
        return Promise.resolve(items);
    }
}

export interface IStorageService extends BaseStorageService {}

class StorageProvider {
    private readonly inMemoryServices = new Map<string, InMemoryStorageService>();
    public get(ns: string, lifecycle: 'memory' | 'cross-tab'): IStorageService {
        switch (lifecycle) {
            case 'memory':
                if (!this.inMemoryServices.has(ns)) {
                    this.inMemoryServices.set(ns, new InMemoryStorageService(ns));
                }
                return this.inMemoryServices.get(ns)!;
            case 'cross-tab':
                return new LocalStorageStorageService(ns);
            default:
                throw new Error(`Unsupported lifecycle: ${lifecycle}`);
        }
    }
}

export class StorageError extends Error {
    constructor(
        message: string,
        public readonly inner: Error,
        public readonly type: 'no-space',
    ) {
        super(message);
        this.name = 'StorageError';
        if (inner) {
            this.stack = `${this.stack}\nCaused by: ${inner.stack}`;
        }
    }
}

export const storageProvider = new StorageProvider();
